import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Shield } from "lucide-react";

export default function AuthenticationTab() {
  return (
    <div className="space-y-6">
      {/* OAuth Device Flow Authentication */}
      <Card className="border-2 border-primary/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            🔐 OAuth Device Flow Authentication
          </CardTitle>
          <CardDescription>
            Seamless authentication like Claude Code - no manual token extraction required!
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="mb-3 p-2 bg-blue-50 dark:bg-blue-900/20 rounded border border-blue-200 dark:border-blue-800">
            <p className="text-xs">
              <strong>📦 No Installation Required!</strong> The LocalDocs CLI is published on npm.
              Just use <code className="bg-muted px-1 rounded">npx</code> to run it directly.
            </p>
          </div>

          <div>
            <p className="font-semibold text-xs mb-1">Step 1: Start Device Flow</p>
            <code className="block bg-muted p-1.5 rounded text-xs font-mono">
              npx @projectassistant/localdocs login
            </code>
            <p className="text-xs text-muted-foreground mt-1">
              CLI displays a 6-character code (e.g., ABC-123) and /cli-auth URL
            </p>
          </div>

          <div>
            <p className="font-semibold text-xs mb-1">Step 2: Approve in Browser</p>
            <p className="text-xs text-muted-foreground">
              Visit <code className="bg-muted px-1 rounded">/cli-auth</code>, enter your code, click
              "Approve"
            </p>
          </div>

          <div>
            <p className="font-semibold text-xs mb-1">Step 3: Done!</p>
            <p className="text-xs text-muted-foreground">
              Access token saved to{" "}
              <code className="bg-muted px-1 rounded">~/.localdocs-cli/config.json</code>
              <br />
              Valid for 30 days - no weekly re-auth needed!
            </p>
          </div>

          <div className="mt-3 p-2 bg-primary/5 rounded text-xs space-y-1">
            <p className="font-semibold">Security Features:</p>
            <ul className="text-muted-foreground space-y-0.5 ml-4 list-disc">
              <li>30-day access tokens with automatic expiration</li>
              <li>Immediate revocation when users removed from team</li>
              <li>User status checked on every request</li>
              <li>SHA-256 token hashing in database</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Security & Privacy */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Security & Privacy
          </CardTitle>
          <CardDescription>How we protect your team's documentation</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="font-semibold mb-2">Authentication & Authorization</h4>
            <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
              <li>
                <strong className="text-foreground">JWT Authentication:</strong> All API requests
                use secure JSON Web Tokens
              </li>
              <li>
                <strong className="text-foreground">Zero Trust Architecture:</strong> User identity
                derived from JWT only (never from client)
              </li>
              <li>
                <strong className="text-foreground">Row-Level Security:</strong> PostgreSQL RLS
                ensures you only see your team's data
              </li>
              <li>
                <strong className="text-foreground">Ownership Verification:</strong> All document
                operations verify ownership before execution
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-2">Data Protection</h4>
            <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
              <li>
                <strong className="text-foreground">SHA-256 Hashing:</strong> Documents identified
                by collision-resistant 64-character hashes
              </li>
              <li>
                <strong className="text-foreground">URL Normalization:</strong> Prevents duplicate
                imports via case-insensitive URL matching
              </li>
              <li>
                <strong className="text-foreground">Encrypted Connections:</strong> All traffic uses
                HTTPS/TLS encryption
              </li>
              <li>
                <strong className="text-foreground">PII Protection:</strong> Email addresses and
                sensitive data sanitized in production logs
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-2">Token Management</h4>
            <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
              <li>
                <strong className="text-foreground">30-Day Validity:</strong> Access tokens remain
                valid for 30 days from issuance
              </li>
              <li>
                <strong className="text-foreground">Automatic Expiration:</strong> Tokens
                automatically expire after 30 days
              </li>
              <li>
                <strong className="text-foreground">Immediate Revocation:</strong> Tokens revoked
                instantly when users are removed from teams
              </li>
              <li>
                <strong className="text-foreground">Status Verification:</strong> User status
                checked on every API request
              </li>
              <li>
                <strong className="text-foreground">Secure Storage:</strong> Tokens hashed with
                SHA-256 before database storage
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-2">Security Score</h4>
            <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-md border border-green-200 dark:border-green-800">
              <p className="text-sm">
                <strong>✅ 100% Security Score</strong> - All 9 critical vulnerabilities eliminated
                through comprehensive security hardening (JWT auth, SHA-256 hashing, zero hard-coded
                credentials, PII sanitization)
              </p>
            </div>
          </div>

          <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md border border-blue-200 dark:border-blue-800">
            <p className="text-sm">
              <strong>🔒 Privacy Note:</strong> Your documentation content is stored securely in
              Supabase PostgreSQL. Only authenticated team members can access your documents. We
              never share your data with third parties.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
